import {
  type AIProviderId,
  aiProviderEndpointUrl,
  configurableAIProviders,
  getAIProvider,
} from "../../../shared/aiProviders";
import { connectorTypeOf } from "../../../shared/connectors";
import { matchesUrlPattern } from "../../../shared/connectors/matchesUrlPattern";
import type { AIConfigDraft } from "./aiProviderDraft";

type ConnectorOption = { identifier: string; urlPattern: string };

// Sibling variants of the named connector serve the same provider unless one
// backs a provider of its own: Bedrock's two auth variants both reach the
// Bedrock surface, so either is offered for `awsBedrock`.
function ownConnectorIdentifiers(provider: AIProviderId): string[] {
  const definition = getAIProvider(provider);
  const identifier = definition.connectorIdentifier;
  if (!definition.endpointFromConnector || !identifier) return [];
  const claimedElsewhere = new Set(
    configurableAIProviders
      .filter((candidate) => candidate.id !== provider)
      .map((candidate) => candidate.connectorIdentifier)
  );
  return (connectorTypeOf(identifier)?.variants ?? [])
    .map((variant) => variant.identifier)
    .filter(
      (variant) => variant === identifier || !claimedElsewhere.has(variant)
    );
}

// A connector that owns the endpoint skips the URL check, since its pattern is
// scoped to its own base URL and needn't match the provider default (an `openai`
// connector patterns `/v1/…`, the provider endpoint is the bare host). A typed
// endpoint keeps the check: the proxy signs only requests matching a pattern.
export function candidateConnectors<T extends ConnectorOption>(
  draft: AIConfigDraft,
  connectors: readonly T[]
): T[] {
  const definition = getAIProvider(draft.provider);
  const endpointUrl = aiProviderEndpointUrl({
    provider: draft.provider,
    baseUrl: definition.packCredentials(draft.credentials).baseUrl,
  });
  const ownTypeIdentifiers = new Set(ownConnectorIdentifiers(draft.provider));
  return connectors.filter(
    (connector) =>
      ownTypeIdentifiers.has(connector.identifier) ||
      (endpointUrl != null &&
        matchesUrlPattern(connector.urlPattern, endpointUrl))
  );
}
